import {useSignal, useSignals} from '@preact/signals-react/runtime'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Button} from 'primereact/button'
import {Divider} from 'primereact/divider'
import {Dropdown} from 'primereact/dropdown'
import {InputText} from 'primereact/inputtext'
import React, {useCallback, useEffect, useMemo} from 'react'
import PhoneInput from 'react-phone-input-2'
import {ReactComponent as PencilIcon} from '../../../src/assets/icons/Pencil.svg'
import {ReactComponent as XmarkIcon} from '../../../src/assets/icons/XMark.svg'
import TranslateUtils from '../../i18n/TranslateUtils'
import {addressTypeOptions, addressTypes} from '../address/AddressConstant'
import {getListGenericAddress} from '../address/AddressEndPoint'
import {useAuth} from '../authentication/AuthProvider'
import {component} from '../form/Constant'

const PATH = 'address'

const CountryPath = '/getCountries'
const StatePath = '/getStates'
const CityPath = '/getCities'

const CountryQueryKey = 'Countries-AddressForm'
const StateQueryKey = 'States-AddressForm'
const CityQueryKey = 'Cities-AddressForm'

const AddressDetailForm = ({
    data = {},
    shouldTextBold = false,
    onChange = () => {},
}) => {
    useSignals()
    const {user} = useAuth()
    const formData = useSignal({...data})
    const refCountry = useSignal()
    const refState = useSignal()
    const switchInputKeys = useSignal([])
    const editField = useSignal(null)
    const queryClient = useQueryClient()
    const isCancelling = useSignal(false)
    const prevData = useSignal(null)

    const {
        translateKeys,
        translateCommons,
        translateOptions,
        translateAddressOptions,
    } = TranslateUtils()

    // reset city khi country/state đổi
    const {mutate: clearCityOptions} = useMutation(() => {}, {
        onSuccess: () => {
            queryClient.setQueryData(
                [
                    CityQueryKey,
                    refCountry.value,
                    refState.value,
                    switchInputKeys.value,
                ],
                () => [],
            )
        },
    })

    // toggle dropdown <-> input text
    const handleSwitchInput = useCallback(
        key => {
            if (switchInputKeys.value?.includes(key)) {
                let onChangeValue = {}
                const switchInputKeysValue = switchInputKeys.value.filter(
                    x => x !== key,
                )
                switchInputKeys.value = switchInputKeysValue

                if (key === 'refState') {
                    refState.value = null
                    onChangeValue = {
                        ...formData.value,
                        refState: null,
                        state: '',
                        city: null,
                        addressConfig: {inputFields: [...switchInputKeysValue]},
                    }
                    clearCityOptions()
                } else if (key === 'city') {
                    onChangeValue = {
                        ...formData.value,
                        city: null,
                        addressConfig: {inputFields: [...switchInputKeysValue]},
                    }
                }

                formData.value = onChangeValue
                onChange(formData.value)
            } else {
                let onChangeValue = {}
                if (
                    key === 'refState' &&
                    !switchInputKeys.value.includes('city')
                ) {
                    const switchInputKeyValue = [
                        ...switchInputKeys.value,
                        key,
                        'city',
                    ]
                    switchInputKeys.value = switchInputKeyValue
                    onChangeValue = {
                        ...formData.value,
                        refState: null,
                        state: '',
                        city: null,
                        addressConfig: {inputFields: switchInputKeyValue},
                    }
                } else {
                    switchInputKeys.value = [...switchInputKeys.value, key]
                    onChangeValue = {
                        ...formData.value,
                        [key]: null,
                        addressConfig: {
                            inputFields: [...switchInputKeys.value],
                        },
                    }
                }
                formData.value = onChangeValue
                onChange(formData.value)
            }
        },
        [switchInputKeys, onChange, formData, clearCityOptions, refState],
    )

    // init khi data thay đổi
    useEffect(() => {
        if (data) {
            const refStateValue = data?.addressConfig?.inputFields?.includes(
                'refState',
            )
                ? data?.state
                : data?.refState

            formData.value = {...data, refState: refStateValue}
            switchInputKeys.value = data?.addressConfig?.inputFields || []
            refCountry.value = data?.refCountry || ''
            refState.value = refStateValue
        }
    }, [data, formData, refCountry, refState, switchInputKeys])

    // fetch country/state/city
    const {data: countryOptions} = useQuery(
        [CountryQueryKey],
        async () => await getAddressData(PATH + CountryPath, user?.id),
        {refetchOnMount: false},
    )

    const {data: stateOptions} = useQuery(
        [StateQueryKey, refCountry.value, switchInputKeys.value],
        async () => await getAddressData(PATH + StatePath, user?.id),
        {
            enabled:
                !!refCountry.value &&
                !switchInputKeys.value.includes('refState'),
            refetchOnMount: false,
        },
    )

    const {data: cityOptions} = useQuery(
        [CityQueryKey, refCountry.value, refState.value, switchInputKeys.value],
        async () => await getAddressData(PATH + CityPath, user?.id),
        {
            enabled:
                !!refCountry.value &&
                !!refState.value &&
                !switchInputKeys.value.includes('refState') &&
                !switchInputKeys.value.includes('city'),
            refetchOnMount: false,
        },
    )

    const getAddressData = async (path, userId) => {
        return await getListGenericAddress(
            path,
            userId,
            refCountry.value,
            refState.value,
        )
    }

    // schema field
    const addressList = useMemo(
        () => [
            {
                key: 'type',
                label: 'Address Type',
                component: component.dropdown,
                options: translateOptions(addressTypeOptions),
                defaultValue: addressTypes.UNKNOWN.value,
            },
            {
                key: 'refCountry',
                label: 'Country',
                component: component.dropdown,
                options: translateAddressOptions(countryOptions, 'country'),
            },
            {
                key: 'refState',
                label: 'State',
                allowInput: true,
                component: component.dropdown,
                options: translateAddressOptions(stateOptions, 'state'),
            },
            {
                key: 'city',
                label: 'City',
                allowInput: true,
                component: component.dropdown,
                options: translateAddressOptions(cityOptions, 'city'),
            },
            {
                key: 'addressLine1',
                label: 'Address Line 1',
                component: component.inputText,
            },
            {
                key: 'addressLine2',
                label: 'Address Line 2',
                component: component.inputText,
            },
            {
                key: 'addressLine3',
                label: 'Address Line 3',
                component: component.inputText,
            },
            {
                key: 'addressLine4',
                label: 'Address Line 4',
                component: component.inputText,
            },
            {
                key: 'postalCode',
                label: 'Postal Code',
                component: component.inputText,
            },
            {key: 'phone', label: 'Phone', component: component.phoneInput},
        ],
        [
            translateOptions,
            translateAddressOptions,
            countryOptions,
            stateOptions,
            cityOptions,
        ],
    )

    const handleDropdownChange = useCallback(
        (key, value) => {
            if (data) {
                if (key === 'refCountry') {
                    refCountry.value = value
                    refState.value = null
                    onChange({
                        ...formData.value,
                        [key]: value,
                        refState: null,
                        city: null,
                    })
                    clearCityOptions()
                    formData.value = {
                        ...formData.value,
                        [key]: value,
                        refState: null,
                        city: null,
                    }
                } else if (key === 'refState') {
                    refState.value = value

                    let stateLabel =
                        stateOptions &&
                        stateOptions?.length &&
                        stateOptions?.find(item => item?.value === data)?.label

                    onChange({
                        ...formData.value,
                        [key]: value,
                        state: switchInputKeys.value.includes('refState')
                            ? value
                            : stateLabel,
                        city: null,
                    })
                    formData.value = {
                        ...formData.value,
                        [key]: value,
                        state: switchInputKeys.value.includes('refState')
                            ? value
                            : stateLabel,
                        city: null,
                    }
                } else {
                    onChange({...formData.value, [key]: value})
                    formData.value = {
                        ...formData.value,
                        [key]: value,
                    }
                }
            }
        },
        [
            stateOptions,
            data,
            refCountry,
            refState,
            onChange,
            formData,
            clearCityOptions,
            switchInputKeys.value,
        ],
    )

    // edit/save/cancel
    const startEdit = fieldKey => {
        isCancelling.value = false
        prevData.value = {...formData.value} // snapshot lúc bắt đầu edit
        editField.value = fieldKey
    }
    const saveEdit = () => {
        editField.value = null
        prevData.value = null // clear snapshot khi đã save
    }

    const cancelEdit = () => {
        isCancelling.value = true
        if (prevData.value) {
            formData.value = {...prevData.value} // rollback snapshot
        } else {
            formData.value = {...data} // fallback nếu chưa có snapshot
        }
        editField.value = null
        prevData.value = null
    }

    const renderInput = item => {
        const handleKeyDown = e => {
            if (e.key === 'Enter') {
                e.preventDefault()
                if (!isCancelling.value) saveEdit(item.key)
            }
        }
        // Nếu field được phép chuyển Input <-> Dropdown
        if (
            item.component === component.dropdown &&
            !switchInputKeys.value.includes(item.key)
        ) {
            return (
                <div className="flex gap-2 text-900 col-12 line-height-3">
                    <div className="w-full p-0">
                        <Dropdown
                            className={`col-12 h-3rem p-0 ${
                                shouldTextBold ? 'font-semibold' : ''
                            }`}
                            inputId={item.key}
                            options={item.options || []}
                            optionLabel="label"
                            optionValue="value"
                            emptyFilterMessage={translateCommons(
                                'No available options',
                            )}
                            emptyMessage={translateCommons('No results found')}
                            filter
                            filterBy="label"
                            value={
                                formData.value ? formData.value[item.key] : ''
                            }
                            onChange={e =>
                                handleDropdownChange(item.key, e.value)
                            }
                            placeholder={translateKeys(item.placeholder)}
                            onHide={() => {
                                if (!isCancelling.value) {
                                    saveEdit(item.key)
                                }
                            }}
                        />
                    </div>
                    {item?.allowInput && (
                        <div className="flex align-items-center">
                            <Button
                                icon={
                                    switchInputKeys.value.includes(item.key)
                                        ? 'pi pi-list'
                                        : 'pi pi-pencil'
                                }
                                className="p-button-plain p-button-rounded"
                                onClick={() => handleSwitchInput(item.key)}
                                tooltip={
                                    switchInputKeys.value.includes(item.key)
                                        ? translateCommons('Switch to dropdown')
                                        : translateCommons('Switch to input')
                                }
                                tooltipOptions={{position: 'top'}}
                            />
                        </div>
                    )}
                </div>
            )
        }

        if (
            item.component === component.inputText ||
            item.component === component.inputNumber ||
            switchInputKeys.value.includes(item.key)
        ) {
            return (
                <div className="flex gap-2 text-900 col-12 line-height-3">
                    <div className="flex-grow-1 p-0">
                        <InputText
                            className={`h-3rem col-12 ${
                                shouldTextBold ? 'font-semibold' : ''
                            }`}
                            value={
                                formData.value && formData.value[item.key]
                                    ? formData.value[item.key]
                                    : ''
                            }
                            onChange={e =>
                                handleDropdownChange(item.key, e.target.value)
                            }
                            onBlur={() => {
                                if (!isCancelling.value) {
                                    saveEdit(item.key)
                                }
                            }}
                            onKeyDown={handleKeyDown}
                        />
                    </div>
                    {item?.allowInput && (
                        <div className="flex-none align-items-center p-0">
                            <Button
                                icon={
                                    switchInputKeys.value.includes(item.key)
                                        ? 'pi pi-list'
                                        : 'pi pi-pencil'
                                }
                                className="p-button-plain p-button-rounded"
                                onClick={() => handleSwitchInput(item.key)}
                                tooltip={
                                    switchInputKeys.value.includes(item.key)
                                        ? translateCommons('Switch to dropdown')
                                        : translateCommons('Switch to input')
                                }
                                tooltipOptions={{position: 'top'}}
                            />
                        </div>
                    )}
                </div>
            )
        }

        if (item.component === component.phoneInput) {
            return (
                <div className="text-900 col-12 line-height-3">
                    <span className="col-12 p-0 p-float-label">
                        <div className="col-12 m-0 p-0">
                            <PhoneInput
                                id={item.key}
                                className={`col-12 h-3rem p-0 ${
                                    shouldTextBold ? 'font-semibold' : ''
                                }`}
                                inputClass={`${
                                    shouldTextBold ? 'font-semibold' : ''
                                }`}
                                placeholder={translateKeys(item.placeholder)}
                                enableSearch
                                country={'gb'}
                                preferredCountries={['gb', 'us', 'ca', 'vn']}
                                inputStyle={{minHeight: '40px'}}
                                alwaysDefaultMask={true}
                                onChange={(value, country, e, formattedData) =>
                                    handleDropdownChange(
                                        item.key,
                                        formattedData,
                                    )
                                }
                                value={
                                    formData.value
                                        ? formData.value[item.key]
                                        : ''
                                }
                                onBlur={() => {
                                    if (!isCancelling.value) {
                                        saveEdit(item.key)
                                    }
                                }}
                                onKeyDown={handleKeyDown}
                            />
                        </div>
                    </span>
                </div>
            )
        }

        return null
    }

    return (
        <div id="profile-contact-information" className="w-full">
            <div className="profile-detail-container">
                {addressList.map(item => (
                    <React.Fragment key={item.key}>
                        <div className="profile-row">
                            <div className="profile-label font-semibold">
                                {translateKeys(item.label)}
                            </div>

                            <div className="profile-value">
                                {editField.value === item.key
                                    ? renderInput(item)
                                    : formData.value[item.key] || '-'}
                            </div>

                            <div className="profile-action">
                                {editField.value === item.key ? (
                                    <>
                                        <Button
                                            className="profile-action-icon text-primary"
                                            icon={<XmarkIcon />}
                                            style={{
                                                marginRight: 8,
                                            }}
                                            onMouseDown={() => {
                                                isCancelling.value = true
                                                cancelEdit()
                                            }}></Button>
                                    </>
                                ) : (
                                    <Button
                                        className="profile-action-icon text-primary"
                                        icon={<PencilIcon />}
                                        style={{
                                            marginRight: 8,
                                        }}
                                        onMouseDown={() => {
                                            startEdit(item.key)
                                        }}></Button>
                                )}
                            </div>
                        </div>
                        <Divider type="dashed" className="m-0 p-0" />
                    </React.Fragment>
                ))}
            </div>
        </div>
    )
}

export default AddressDetailForm
